import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Warning } from '@mui/icons-material';
import { authApi } from '../../services/api/authApi';

interface TwoFactorDisableDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const TwoFactorDisableDialog: React.FC<TwoFactorDisableDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisable = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authApi.disable2FA({ password });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable 2FA. Please check your password.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setShowPassword(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning color="warning" />
        Disable Two-Factor Authentication
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Disabling 2FA will make your account less secure. You will no longer need a verification code to sign in.
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          To confirm, enter your account password.
        </Typography>

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && password) {
              handleDisable();
            }
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDisable}
          disabled={!password || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Disable 2FA'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
